import React, { useState } from 'react';
import { Card, Row, Col, Badge, Carousel, Alert, Button } from 'react-bootstrap';
import { 
  FaBed, FaBath, FaRuler, FaHome, FaCalendar, 
  FaMapMarkerAlt, FaCamera, FaDollarSign, FaExternalLinkAlt,
  FaSwimmingPool, FaWarehouse
} from 'react-icons/fa';
import { DealDetail } from '../../services/dealService';
import dealService from '../../services/dealService';
import './DealMLSDetails.css';

interface DealMLSDetailsProps {
  deal: DealDetail;
  mlsImages?: string[];
  mlsData?: any;
  listingAgent?: {
    name: string;
    officeName: string;
  };
  propertyDetails?: {
    propertyType: string;
    daysOnMarket: number;
    lotSize: number;
  };
}

const DealMLSDetails: React.FC<DealMLSDetailsProps> = ({ deal, mlsImages = [], mlsData, listingAgent, propertyDetails }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  // Use provided MLS images directly
  const images = mlsImages && mlsImages.length > 0 ? mlsImages : [];
  const hasImages = images.length > 0;

  // Format address for display
  const formatAddress = () => {
    if (deal.unparsed_address) return deal.unparsed_address;
    if (deal.address) return deal.address;
    return `${deal.city}, AZ ${deal.postal_code}`;
  };

  return (
    <Card className="mb-4">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">
          <FaHome className="me-2" />
          MLS Listing Details
        </h5>
      </Card.Header>
      <Card.Body>
        <Row>
          {/* Left Column - Images */}
          <Col lg={7}>
            {hasImages ? (
              <div className="image-gallery-section">
                <Carousel 
                  activeIndex={activeImageIndex} 
                  onSelect={setActiveImageIndex}
                  interval={null}
                >
                  {images.map((image, index) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100 gallery-image"
                        src={image}
                        alt={`Property Image ${index + 1}`}
                      />
                      <Carousel.Caption className="bg-dark bg-opacity-50 rounded">
                        <p className="mb-0">
                          <FaCamera className="me-1" />
                          Image {index + 1} of {images.length}
                        </p>
                      </Carousel.Caption>
                    </Carousel.Item>
                  ))}
                </Carousel>
                
                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="thumbnail-strip">
                    {images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                        onClick={() => setActiveImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Alert variant="info">
                <FaCamera className="me-2" />
                MLS photos not available for this property
              </Alert>
            )}

            {/* Property Features Grid */}
            <Row className="g-3">
              <Col md={6}>
                <Card className="h-100 border-secondary">
                  <Card.Body>
                    <h6 className="text-muted mb-3">Property Details</h6>
                    <div className="mb-2">
                      <strong>Type:</strong> {propertyDetails?.propertyType || deal.property_subtype || 'Single Family'}
                    </div>
                    <div className="mb-2">
                      <strong>Year Built:</strong> {deal.year_built || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Lot Size:</strong> {propertyDetails?.lotSize ? `${propertyDetails.lotSize.toLocaleString()} sqft` : deal.lot_size_sqft ? `${deal.lot_size_sqft.toLocaleString()} sqft` : 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Pool:</strong> {deal.pool_yn ? (
                        <Badge bg="success"><FaSwimmingPool className="me-1" />Yes</Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </div>
                    <div className="mb-2">
                      <strong>County:</strong> {deal.county || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Subdivision:</strong> {deal.subdivision_name || 'N/A'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="h-100 border-secondary">
                  <Card.Body>
                    <h6 className="text-muted mb-3">Listing Information</h6>
                    <div className="mb-2">
                      <strong>MLS #:</strong> {deal.listing_id}
                    </div>
                    <div className="mb-2">
                      <strong>List Price:</strong> <span className="text-primary fw-bold">{dealService.formatCurrency(deal.list_price)}</span>
                    </div>
                    <div className="mb-2">
                      <strong>Original Price:</strong> {dealService.formatCurrency(deal.original_list_price)}
                    </div>
                    <div className="mb-2">
                      <strong>Days on Market:</strong> <Badge bg="info">{propertyDetails?.daysOnMarket || mlsData?.days_on_market || deal.days_on_market || 0} days</Badge>
                    </div>
                    <div className="mb-2">
                      <strong>$/Sq Ft:</strong> ${Math.round((deal.list_price || 0) / (deal.living_area || 1))}
                    </div>
                    {deal.list_price !== deal.original_list_price && (
                      <div className="mb-2">
                        <strong>Price Reduction:</strong> 
                        <Badge bg="danger" className="ms-2">
                          {dealService.formatCurrency((deal.original_list_price || 0) - (deal.list_price || 0))}
                        </Badge>
                      </div>
                    )}
                    {listingAgent && (
                      <>
                        <hr className="my-2" />
                        <div className="mb-2 text-muted small">
                          <strong>Courtesy of:</strong>
                          <div>{listingAgent.name}</div>
                          <div>{listingAgent.officeName}</div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>

          {/* Right Column - Quick Stats and Analysis */}
          <Col lg={5}>
            {/* Property Stats */}
            <div className="bg-light rounded p-4 mb-4">
              <h5 className="mb-3">
                <FaMapMarkerAlt className="me-2 text-primary" />
                {formatAddress()}
              </h5>
              
              <Row className="g-3 mb-3">
                <Col xs={4} className="text-center">
                  <div className="border rounded p-3 bg-white">
                    <FaBed className="text-primary mb-2" size={24} />
                    <h4 className="mb-0">{deal.bedrooms || 0}</h4>
                    <small className="text-muted">Bedrooms</small>
                  </div>
                </Col>
                <Col xs={4} className="text-center">
                  <div className="border rounded p-3 bg-white">
                    <FaBath className="text-primary mb-2" size={24} />
                    <h4 className="mb-0">{deal.bathrooms || 0}</h4>
                    <small className="text-muted">Bathrooms</small>
                  </div>
                </Col>
                <Col xs={4} className="text-center">
                  <div className="border rounded p-3 bg-white">
                    <FaRuler className="text-primary mb-2" size={24} />
                    <h4 className="mb-0">{deal.living_area?.toLocaleString() || 0}</h4>
                    <small className="text-muted">Sq Ft</small>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Deal Analysis Summary */}
            <Card className="border-primary">
              <Card.Header className="bg-primary text-white">
                <h6 className="mb-0">Deal Analysis Summary</h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>List Price:</span>
                    <strong>{dealService.formatCurrency(deal.list_price)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Predicted Value:</span>
                    <strong className="text-success">{dealService.formatCurrency(deal.predicted_value)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>ARV (Renovated):</span>
                    <strong className="text-info">{dealService.formatCurrency(deal.renovated_arv)}</strong>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-2">
                    <span>Price Gap:</span>
                    <strong className={deal.price_gap_pct < 0 ? 'text-success' : 'text-danger'}>
                      {deal.price_gap_pct}% ({dealService.formatCurrency(deal.price_gap_dollars)})
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Deal Score:</span>
                    <Badge bg={deal.deal_score >= 80 ? 'success' : deal.deal_score >= 60 ? 'warning' : 'danger'}>
                      {deal.deal_score}/100
                    </Badge>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <Button variant="primary" size="sm">
                    <FaExternalLinkAlt className="me-2" />
                    View Full MLS Listing
                  </Button>
                  <Button variant="outline-secondary" size="sm">
                    <FaWarehouse className="me-2" />
                    Schedule Property Tour
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default DealMLSDetails;